import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import type { Tone } from "../design/tone";
import { useSession } from "../session/SessionProvider";
import { when } from "../system/format";

// ---------------------------------------------------------------------------
// Sweep §4 / 0215. APPLICANTS — people who asked this agency for work through
// its public link. Accepting puts them on the bench and invites them to sign in,
// in one step; declining takes a reason from the closed list. A waiting or
// declined application is deleted six months after it was sent.
//
// State words, tones and whether a decision may still be made all arrive on the
// row; nothing about them is spelled here.
// ---------------------------------------------------------------------------

type Application = {
  id: string;
  given_name: string;
  family_name: string;
  email: string;
  phone: string | null;
  home_locality: string | null;
  roles: string[];
  right_to_work: boolean;
  submitted_at: string;
  state_label: string;
  state_tone: Tone;
  is_open: boolean;
  decline_reason_label: string | null;
  decided_at: string | null;
  decided_by_you: boolean | null;
  worker_id: string | null;
};

type Inbox = {
  applications: Application[];
  reasons: { id: string; label: string }[];
  link: { token: string; created_at: string } | null;
  may_erase: boolean;
};

export function Applicants() {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<Inbox> & { error?: { message?: string } }>(console_.applications(apiBase));
      if (res.status === 200 && Array.isArray(res.body.applications)) {
        setInbox(res.body as Inbox);
        setFailed(null);
      } else {
        // NULL, NOT AN EMPTY INBOX: "nobody applied" and "this could not be read" differ.
        setInbox(null);
        setFailed(refusal(res.body.error) ?? t("The applications could not be read."));
      }
    } catch {
      setInbox(null);
      setFailed(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const waiting = inbox ? inbox.applications.filter((a) => a.is_open).length : 0;

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("Applicants")}</h1>
        {waiting > 0 ? <StatusChip tone="attention" label={t("{n} waiting", { n: waiting })} /> : null}
      </div>
      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not available")} />
          <span>{failed}</span>
        </div>
      ) : null}
      {inbox ? <LinkPanel link={inbox.link} onChanged={load} /> : null}
      {inbox && inbox.applications.length === 0 ? (
        <section className="panel">
          <p className="muted">{t("Nobody has applied yet. Share the link above to start taking applications.")}</p>
        </section>
      ) : null}
      {inbox && inbox.applications.length > 0 ? (
        <ul className="plain-list card-list">
          {inbox.applications.map((a) => (
            <ApplicationRow key={a.id} application={a} reasons={inbox.reasons} mayErase={inbox.may_erase} onChanged={load} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LinkPanel({ link, onChanged }: { link: Inbox["link"]; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const address = link ? `${window.location.origin}/apply/${link.token}` : null;

  async function act(spec: ReturnType<typeof console_.openApplicationLink>) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(spec);
      if (res.status !== 200 && res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("The link could not be changed."));
        return;
      }
      await onChanged();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack">
      <h2>{t("Your application link")}</h2>
      {address ? (
        <>
          <p className="muted">
            {t("Anyone with this link can apply. Put it on your website or a poster. Opening a new link stops the old one working.")}
          </p>
          <div className="field">
            <label htmlFor="application-link">{t("Link")}</label>
            <input id="application-link" readOnly value={address} onFocus={(e) => e.target.select()} />
            <p className="field-hint">{t("Open since {when}.", { when: when(link!.created_at) })}</p>
          </div>
        </>
      ) : (
        <p className="muted">{t("You are not taking applications. Open a link to let people apply.")}</p>
      )}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not changed")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={() => void act(console_.openApplicationLink(apiBase))}>
          {address ? t("Open a new link") : t("Open an application link")}
        </button>
        {address ? (
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void act(console_.closeApplicationLink(apiBase))}>
            {t("Stop taking applications")}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function ApplicationRow({
  application: a,
  reasons,
  mayErase,
  onChanged,
}: {
  application: Application;
  reasons: Inbox["reasons"];
  mayErase: boolean;
  onChanged: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [reason, setReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Erasing deletes the row outright, so the first click only asks (in-page, never window.confirm).
  const [confirming, setConfirming] = useState(false);

  async function act(spec: ReturnType<typeof console_.acceptApplication>) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(spec);
      if (res.status !== 200 && res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("That could not be saved."));
        return;
      }
      await onChanged();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const name = `${a.given_name} ${a.family_name}`;
  return (
    <li className="panel stack">
      <div className="panel-head">
        <h3>{name}</h3>
        <StatusChip tone={a.state_tone} label={translateLabel(locale, a.state_label)} />
      </div>
      <p>
        {a.roles.map((r) => translateLabel(locale, r)).join(", ")} · {a.email}
        {a.phone ? ` · ${a.phone}` : ""}
        {a.home_locality ? ` · ${a.home_locality}` : ""}
      </p>
      <p className="muted">
        {a.right_to_work
          ? t("Sent {when}. Says they have the right to work here.", { when: when(a.submitted_at) })
          : t("Sent {when}. Says they do not have the right to work here.", { when: when(a.submitted_at) })}
        {a.decided_at
          ? ` ${a.decided_by_you ? t("Decided {when} by you.", { when: when(a.decided_at) }) : t("Decided {when}.", { when: when(a.decided_at) })}`
          : ""}
        {a.decline_reason_label ? ` ${t("Reason: {reason}.", { reason: translateLabel(locale, a.decline_reason_label) })}` : ""}
      </p>
      {a.worker_id ? (
        <p>
          <Link to={`/console/workers/${a.worker_id}`}>{t("Open their bench record")}</Link>
        </p>
      ) : null}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not saved")} />
          <span>{notice}</span>
        </div>
      ) : null}
      {a.is_open && !declining ? (
        <div className="row">
          <button type="button" className="btn" disabled={busy} onClick={() => void act(console_.acceptApplication(apiBase, a.id))}>
            {t("Add to the bench and invite")}
          </button>
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setDeclining(true)}>
            {t("Decline")}
          </button>
        </div>
      ) : null}
      {a.is_open && declining ? (
        <div className="row">
          <div className="field">
            <label htmlFor={`decline-${a.id}`}>{t("Reason to decline")}</label>
            <select id={`decline-${a.id}`} value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">{t("Choose…")}</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {translateLabel(locale, r.label)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy || reason === ""}
            onClick={() => void act(console_.declineApplication(apiBase, a.id, reason))}
          >
            {t("Decline")}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => {
              setDeclining(false);
              setReason("");
            }}
          >
            {t("Cancel")}
          </button>
        </div>
      ) : null}
      {mayErase && !a.worker_id ? (
        <div className="row">
          {confirming ? (
            <>
              <span>{t("Erase this application now? Everything {name} sent is deleted at once and cannot be brought back.", { name })}</span>
              <button type="button" className="btn" disabled={busy} onClick={() => void act(console_.eraseApplication(apiBase, a.id))}>
                {busy ? t("Erasing…") : t("Yes, erase it")}
              </button>
              <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
                {t("Keep it")}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setConfirming(true)}>
              {t("Erase this application now")}
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}
